import React, { useState, useLayoutEffect, useEffect } from 'react';
import * as Yup from 'yup';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik, FormikErrors } from 'formik';
import {
  Box,
  Typography,
  Link,
  Button,
  CircularProgress,
} from '@mui/material';
import { CircularButton, InputText, Spinner } from '../../components';
import { HSLogoColorIcon } from '../../assets/icons';
import { AppThunkDispatch } from '../../interfaces/redux.interface';
import { FormValidations } from '../../utils';
import { authActions, selectIsLoading } from '../../redux/authSlice';
import { paths } from '..';
import authService from '../../services/auth.service';
import useStyles from './VerifyEmail.styles';


const awsVerifyEmailError = 'Invalid code provided, please request a code again.';

const VerifyEmail = () => {
  const classes = useStyles();
  const [submitted, setSubmitted] = useState(false);
  const dispatch: AppThunkDispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);
  const navigate = useNavigate();
  const [cognitoUser] = authService.useCognitoUserState();

  useLayoutEffect(() => {
    if (!cognitoUser) {
      navigate(paths.calculatorDashboard);
    }
    return () => {
      if (!cognitoUser) {
        dispatch(authActions.signOut());
      }
    };
  }, [cognitoUser, navigate, dispatch]);

   useEffect(()=>{
     dispatch(authActions.resendCode());
   },[])

  const initialValues = {
    code: '',
    aws: false,
  };

  const onSubmit = (
    values: typeof initialValues,
    { setErrors }: { setErrors: (errors: FormikErrors<typeof initialValues>) => void },
  ) => {
    setErrors({ aws: '' });
    setSubmitted(true);

    dispatch(authActions.verifyEmail(values.code))
      .then(() => {
        navigate(paths.calculatorDashboard);
      })
      .catch(() => {
        setErrors({ aws: awsVerifyEmailError });
        setSubmitted(false);
      });
  };

  const validationSchema = Yup.object().shape({
    code: FormValidations.required('Verification Code'),
  });

  const form = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <>
      <Box className={classes.root}>
        <Box className={classes.containerColumn}>
          <Box className={classes.main}>
            <HSLogoColorIcon className={classes.zeroLogo} />
            <Typography variant="h5" className={classes.title}>
              Verify Your Email
            </Typography>
            <Typography variant="body2" className={classes.note}>
              Enter your verification code
            </Typography>
            <form onSubmit={form.handleSubmit}>
              <InputText
                id="code"
                name="code"
                onChange={form.handleChange}
                value={form.values.code}
                labelText="Verification Code"
                placeholder="Enter Verification Code"
                errorMessage={form.errors.code || form.errors.aws}
                customClass={classes.inputMail}
              />
              <Box>
                <CircularButton
                  color="primary"
                  type="submit"
                  variant="contained"
                  customClass={classes.submitButtonStyles}
                  disabled={submitted}
                >
                  Verify EMAIL
                </CircularButton>
              </Box>
            </form>
            <Box className={classes.resendCode}>
              <Typography
                variant="body2"
                className={classes.option}
                onClick={() => dispatch(authActions.resendCode())}
              >
                Send the code again
              </Typography>
            </Box>
            <Box className={classes.loginLinkContainer}>
              <Link
                component={RouterLink}
                to={paths.signIn}
                className={classes.loginLink}
                onClick={() => dispatch(authActions.signOut())}
              >
                Return To Login
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
      <Spinner show={isLoading} />
    </>
  );
};

export default VerifyEmail;
